<script setup lang="ts">
import type { VNode } from 'vue';
import { RouterView } from 'vue-router';
import CachedRouteViewPage from '@/components/navigation/CachedRouteViewPage.vue';

defineProps<{
  cacheScope?: PropertyKey;
  contentClass?: string;
  preserveState?: boolean;
}>();

const componentKeys = new WeakMap<object, number>();
let nextComponentKey = 0;

function getComponentKey(component: VNode | undefined): PropertyKey {
  const type = component?.type;
  if ((typeof type === 'object' && type !== null) || typeof type === 'function') {
    let key = componentKeys.get(type);
    if (key === undefined) {
      key = nextComponentKey++;
      componentKeys.set(type, key);
    }
    return key;
  }
  return type ?? 'empty-route-view';
}
</script>

<template>
  <RouterView v-slot="{ Component, route }">
    <template v-if="preserveState">
      <Transition name="route-view">
        <KeepAlive
          :key="cacheScope"
          :max="2"
        >
          <CachedRouteViewPage
            v-if="Component && route.meta.pageStateKey"
            :key="route.meta.pageStateKey"
            :component="Component"
            :content-class="contentClass"
            :route="route"
          />
        </KeepAlive>
      </Transition>
      <Transition name="route-view">
        <div
          v-if="Component && !route.meta.pageStateKey"
          :key="getComponentKey(Component)"
          class="route-view-page flex w-full min-w-0 flex-col"
          :class="contentClass"
        >
          <component :is="Component" />
        </div>
      </Transition>
    </template>
    <Transition
      v-else
      name="route-view"
    >
      <div
        v-if="Component"
        :key="getComponentKey(Component)"
        class="route-view-page flex w-full min-w-0 flex-col"
        :class="contentClass"
      >
        <component :is="Component" />
      </div>
    </Transition>
  </RouterView>
</template>
